import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { adminApps, type AdminApp } from '@app/apps';

interface AppSwitcherProps {
  currentApp: AdminApp;
  onSwitch?: () => void;
}

/**
 * Switches between the Budgeting and Accounting admin apps.
 *
 * Both live in this site, so choosing one is an internal navigation to that
 * app's home page rather than a page load.
 */
export function AppSwitcher({ currentApp, onSwitch }: AppSwitcherProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Close on an outside click or Escape — the two ways people expect to dismiss
  // a menu. Escape also returns focus to the trigger, so keyboard users are not
  // dropped at the top of the document.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const selectApp = (app: AdminApp) => {
    if (!app.enabled) {
      return;
    }

    setIsOpen(false);

    if (app.id !== currentApp.id) {
      navigate(app.homePath);
    }

    onSwitch?.();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        className={cn(
          'flex items-center gap-2 rounded-lg py-1.5 pl-1.5 pr-2 text-left transition-colors',
          isOpen ? 'bg-white/15' : 'hover:bg-white/10',
        )}
      >
        <img
          src={currentApp.iconSrc}
          alt=""
          aria-hidden
          className="h-7 w-7 shrink-0 rounded-md object-contain"
        />

        <span className="truncate text-sm font-semibold text-white">{currentApp.label}</span>

        <ChevronDown
          aria-hidden
          className={cn(
            'h-4 w-4 shrink-0 text-navy-200 transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Switch app"
          className="absolute left-0 top-full z-50 mt-1 w-72 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {adminApps.map((app) => {
            const isCurrent = app.id === currentApp.id;

            return (
              <button
                key={app.id}
                type="button"
                role="menuitem"
                disabled={!app.enabled}
                aria-disabled={!app.enabled}
                onClick={() => selectApp(app)}
                className={cn(
                  'flex w-full items-start gap-2.5 px-2.5 py-2 text-left transition-colors',
                  app.enabled ? 'hover:bg-slate-50' : 'cursor-not-allowed',
                )}
              >
                <img
                  src={app.iconSrc}
                  alt=""
                  aria-hidden
                  className={cn(
                    'mt-0.5 h-8 w-8 shrink-0 rounded-md object-contain',
                    !app.enabled && 'opacity-40 grayscale',
                  )}
                />

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'truncate text-sm font-medium',
                        app.enabled ? 'text-slate-900' : 'text-slate-400',
                      )}
                    >
                      {app.label}
                    </span>
                    {!app.enabled && (
                      <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
                        Coming soon
                      </span>
                    )}
                  </span>
                  <span
                    className={cn(
                      'block text-xs leading-snug',
                      app.enabled ? 'text-slate-500' : 'text-slate-400',
                    )}
                  >
                    {app.description}
                  </span>
                </span>

                {isCurrent && app.enabled && (
                  <Check aria-hidden className="mt-1.5 h-4 w-4 shrink-0 text-brand-600" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
